interface Props {
  onPress: (key: string) => void;
  disabled?: boolean;
}

const KEYS = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['*', '0', '#'],
];

const LETTERS: Record<string, string> = {
  '2': 'ABC', '3': 'DEF', '4': 'GHI', '5': 'JKL',
  '6': 'MNO', '7': 'PQRS', '8': 'TUV', '9': 'WXYZ',
};

// KLIENTO softphone palette (matches the Chrome dialer extension). Kept local so
// the keypad looks identical whether or not the host page defines these tokens.
const K = {
  key: '#ffffff',
  keyBorder: '#E6ECEF',
  keyHoverBg: '#F0F9FD',
  keyHoverBorder: '#ABDEF2',
  keyActiveBg: '#E0F3FA',
  ink: '#16242E',
  mono: "'JetBrains Mono', var(--font-mono, ui-monospace), monospace",
};

export function Dialpad({ onPress, disabled }: Props) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 7,
        width: '100%',
        // Force LTR fill order — in the RTL document the grid otherwise fills
        // right-to-left, rendering the keypad mirrored (3-2-1 / 6-5-4). A phone
        // keypad is always 1-2-3 left-to-right regardless of UI language.
        direction: 'ltr',
      }}
    >
      {KEYS.flat().map((key) => (
        <button
          key={key}
          type="button"
          onClick={() => onPress(key)}
          disabled={disabled}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: 54,
            padding: '12px 0',
            border: `1px solid ${K.keyBorder}`,
            borderRadius: 10,
            background: K.key,
            color: K.ink,
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : 1,
            transition: 'background 120ms ease, border-color 120ms ease, transform 60ms ease',
            fontFamily: K.mono,
          }}
          onMouseEnter={(e) => {
            if (disabled) return;
            e.currentTarget.style.background = K.keyHoverBg;
            e.currentTarget.style.borderColor = K.keyHoverBorder;
          }}
          onMouseDown={(e) => {
            e.currentTarget.style.background = K.keyActiveBg;
            e.currentTarget.style.transform = 'scale(0.97)';
          }}
          onMouseUp={(e) => {
            e.currentTarget.style.background = K.keyHoverBg;
            e.currentTarget.style.transform = '';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = K.key;
            e.currentTarget.style.borderColor = K.keyBorder;
            e.currentTarget.style.transform = '';
          }}
        >
          <span style={{ fontSize: 17, fontWeight: 500, lineHeight: 1, fontFamily: K.mono }}>{key}</span>
          {LETTERS[key] && (
            <span style={{ fontSize: 9, color: '#93A1AB', marginTop: 3, letterSpacing: '0.06em', fontFamily: 'var(--font-sans, sans-serif)' }}>{LETTERS[key]}</span>
          )}
        </button>
      ))}
    </div>
  );
}

export function DirIcon({ direction }: { direction: 'inbound' | 'outbound' | 'internal' }) {
  return (
    <span aria-label={direction} style={{ display: 'inline-block' }}>
      {direction === 'inbound' ? '↙' : direction === 'outbound' ? '↗' : '↔'}
    </span>
  );
}
